import React from 'react';
import classnames from 'classnames';
import './chipBuilder.scss';

const className = 'chip-builder';

function mapChipToHTML(chip) {
  return [
    `<span class="${className}__chip" data-chip>`,
    `<span class="${className}__chip-text">`,
    `${chip}`,
    `<span class="${className}__chip-comma" contenteditable="false">,</span>`,
    `</span>`,
    `</span>`,
  ].join('');
}

function parseAsChips(text) {
  if (text.trim().length === 0) {
    return [];
  }

  return text
    .replace(/\n/g, ',')
    .split(',')
    .map((str) => str.trim().replace(/\s+/g, ' '))
    .filter((val) => val.length);
}

function selectEnd(target) {
  setTimeout(() => {
    const range = document.createRange();
    const sel = window.getSelection();
    range.selectNodeContents(target);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    target.focus();
  }, 0);
}

function dedupe(arr1, ar2) {
  return Array.from(new Set(arr1.concat(ar2))); // eslint-disable-line compat/compat
}

export class ChipBuilder extends React.Component {
  constructor(props) {
    const { chips = [], onChange = () => {} } = props;
    super(props);
    this.state = {
      chips: [...chips],
    };
    this.onChange = onChange;
    this.editableArea = null;
  }

  componentDidMount() {
    this.editableArea.addEventListener('click', this.onClick);
  }

  componentWillUnmount() {
    this.editableArea.removeEventListener('click', this.onClick);
  }

  onPaste = (e) => {
    const { disabled } = this.props;
    if (disabled) {
      return true;
    }

    const text = (e.originalEvent || e).clipboardData.getData('text/plain');
    const chips = parseAsChips(text);

    this.setState((prevState) => {
      const dedupedChips = dedupe(prevState.chips, chips);
      this.onChange(dedupedChips);

      return {
        chips: dedupedChips,
      };
    });

    selectEnd(e.currentTarget);

    e.preventDefault();
    return true;
  };

  onKeyUp = ({ currentTarget }) => {
    const { disabled } = this.props;
    if (disabled) {
      return true;
    }

    const text = currentTarget.innerText;

    if (text.trim().length === 0) {
      this.setState({
        chips: [],
      });
      this.onChange([]);
      selectEnd(currentTarget);
    }
    return true;
  };

  onKeyPress = (e) => {
    const { disabled } = this.props;
    if (disabled) {
      return true;
    }

    const isTriggerKey = [',', 'Enter'].includes(e.key);

    if (!isTriggerKey) {
      return true;
    }

    e.preventDefault();

    const { currentTarget } = e;
    const text = currentTarget.innerText;

    this.process(text);

    selectEnd(currentTarget);

    return true;
  };

  onClick = (e) => {
    const { disabled } = this.props;
    if (disabled) {
      return true;
    }

    const { target, currentTarget } = e;

    if (target.hasAttribute('data-chip')) {
      const upToDateChips = parseAsChips(currentTarget.innerText); // Extract chips to insure recent edits are included
      const text = target.textContent.replace(',', '');
      const index = upToDateChips.indexOf(text);

      if (index >= 0) {
        const chips = upToDateChips;
        chips.splice(index, 1);
        this.setState({
          chips,
        });
        this.onChange(chips);
        selectEnd(currentTarget);
      }
    }

    return true;
  };

  onBlur = (e) => {
    const { disabled } = this.props;
    if (disabled) {
      return true;
    }

    e.preventDefault();

    const { currentTarget } = e;
    const text = currentTarget.innerText;

    this.process(text);

    return true;
  };

  process = (text, chips = parseAsChips(text)) => {
    // const chips = parseAsChips(text);
    this.setState({
      chips,
    });
    this.onChange(chips);
  };

  render() {
    const { disabled, placeholder = '' } = this.props;
    const html = this.state.chips
      .map(mapChipToHTML)
      .join(
        `<span class="${className}--span">&nbsp;&nbsp;<span class="${className}__chip-comma" contenteditable="false">,</span></span>`,
      );

    return (
      <div>
        <div
          role="textbox"
          tabIndex={disabled ? null : 0}
          aria-label={placeholder}
          ref={(el) => {
            this.editableArea = el;
          }}
          data-placeholder={placeholder}
          data-editablearea
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck="false"
          onPaste={this.onPaste}
          onKeyUp={this.onKeyUp}
          onKeyPress={this.onKeyPress}
          onBlur={this.onBlur}
          className={classnames({
            [className]: true,
            [`${className}--disabled`]: disabled,
            [`${className}--empty`]: !this.state.chips.length,
          })}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: `${html}<span class="${className}--span">&nbsp;&nbsp;</span>`,
          }}
        />
      </div>
    );
  }
}
